import React, { useState, useEffect } from 'react';
import { UserOrganisation } from '../../services/userOrganisationsService';

interface EditUserOrganisationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UserOrganisationUpdateInput) => Promise<void>;
  userOrganisation: UserOrganisation | null;
}

interface UserOrganisationUpdateInput {
  user_id: string;
  organisation_id: string;
  role: string;
  active: boolean;
}


const EditUserOrganisationModal: React.FC<EditUserOrganisationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  userOrganisation
}) => {
  const [formData, setFormData] = useState<UserOrganisationUpdateInput>({
    user_id: '',
    organisation_id: '',
    role: '',
    active: true
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);


  // Populate form when userOrganisation changes
  useEffect(() => {
    if (userOrganisation) {
      setFormData({
        user_id: userOrganisation.user_id,
        organisation_id: userOrganisation.organisation_id,
        role: userOrganisation.role,
        active: userOrganisation.active
      });
      setErrors({});
    }
  }, [userOrganisation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.role.trim()) newErrors.role = 'Role is required';

    // Role validation not needed since we use dropdown

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error updating user organisation:', errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof UserOrganisationUpdateInput, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Edit User Organization</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User ID - Read Only Display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User
            </label>
            <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900">
              {userOrganisation ? (
                `${userOrganisation.user_name || ''}${userOrganisation.user_email ? ` (${userOrganisation.user_email})` : ''}`.trim() || userOrganisation.user_id
              ) : 'No user data'}
            </div>
            <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
          </div>

          {/* Organisation Name - Read Only Display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization
            </label>
            <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900">
              {userOrganisation ? (
                userOrganisation.organisation_name || userOrganisation.organisation_id
              ) : 'No organization data'}
            </div>
            <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
          </div>

          {/* Role - Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a role...</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            {errors.role && (
              <p className="text-red-500 text-xs mt-1">{errors.role}</p>
            )}
          </div>

          {/* Active - Toggle */}
          <div>
            <label className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active</span>
              <button
                type="button"
                onClick={() => handleInputChange('active', !formData.active)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
              <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              Update Data
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserOrganisationModal;
